import React, { useEffect, useState, useRef } from 'react';
import { Button, Col, Container, Form, Image, InputGroup, Row, FloatingLabel } from 'react-bootstrap';
import styles from '../ContentManagement.module.css';
import { CheckIcon, LeftArrowIcon, UnionIcon } from '../../../components/icons/Icons';
import { useNavigate } from 'react-router-dom';
import useAxios from '../../../services/AxiosConfig';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedItem } from '../../../components/PerinHealthGridSlice';
import { setReloadGrid } from '../ContentManagementSlicer';
import { setFormValue, setOrgGroups } from './AddEditContentSlice';
import FileUpload from './FileUpload';
import managementStyles from '../../ManagementPages.module.css';
import { showAlert } from '../../../components/PerinHealthAlertSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const AddEditContent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    const [file, setFile] = useState({});
    const [selectedContentType, setSelectedContentType] = useState('');
    const [isLinkInputVisible, setIsLinkInputVisible] = useState(false);
    const [fileValidationError, setFileValidationError] = useState('');
    const contentManagementState = useSelector((state) => state.addEditContent);
    const organizationsHierarchy = useSelector((state) => state.userInfo.organizationsHierarchy);
    const perinHealthGrid = useSelector((state) => state.perinHealthGrid);
    const orgGroups = contentManagementState.orgGroups;
    const selectedItem = perinHealthGrid.selectedItem;
    const isAdmin = useSelector((state) => state.userInfo.isAdmin);
    const dropdowns = useSelector((state) => state.dropdowns);

    const formRef = useRef(null);
    const [validated, setValidated] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [linkVerifying, setLinkVerifying] = useState(false);
    const [linkVerificationError, setLinkVerificationError] = useState('');
    const [linkVerified, setLinkVerified] = useState(false);

    const [selectedOrgId, setSelectedOrgId] = useState('');
    const [selectedGroupId, setSelectedGroupId] = useState('');

    const isEditMode = !!selectedItem && Object.keys(selectedItem).length > 0;

    const selectedOrgIdRef = useRef(null);

    useEffect(() => {
        if (selectedItem && Object.keys(selectedItem).length !== 0) {
            const fileData = selectedItem.file || {};
            setFile(fileData);

            if (selectedItem.linkUrl) {
                dispatch(setFormValue({ name: 'link', value: selectedItem.linkUrl }));
                setIsLinkInputVisible(true);
                setLinkVerified(true);
                setLinkVerificationError('');
                setSelectedContentType('link');
            }

            handleOrganizationChange({ target: { value: selectedItem.organizationId } });
            setSelectedOrgId(selectedItem.organizationId);
            setSelectedGroupId(selectedItem.groupId);
        } else {
            if (organizationsHierarchy.length === 1) {
                selectedOrgIdRef.current = organizationsHierarchy[0].organizationId;
                setSelectedOrgId(selectedOrgIdRef.current);
                handleOrganizationChange({ target: { value: selectedOrgIdRef.current } });
            }
        }
    }, [selectedItem, dispatch, organizationsHierarchy]);

    const onBackHandler = () => {
        dispatch(setSelectedItem({}));
        dispatch(setReloadGrid(true));
        navigate('/content-management');
    };

    const handleFileDrop = (acceptedFiles) => {
        const newFile = acceptedFiles.length > 0 ? acceptedFiles[0] : {};
        if (newFile) {
            newFile.url = URL.createObjectURL(newFile);
            setFile(newFile);
            setFileValidationError(''); // Clear any previous validation errors
        }
    };

    const handleFileValidationError = (errorMessage) => {
        setFileValidationError(errorMessage);
        setFile({}); // Clear the file if validation fails
    };

    const onInputChange = (e) => {
        let { name, value } = e.target;

        if (name === 'groupId') {
            const updatedSelectedItem = { ...selectedItem };

            organizationsHierarchy.forEach((organization) => {
                organization.groups.forEach((group) => {
                    updatedSelectedItem.groupId = group.groupId;

                    if (group.subgroups) {
                        group.subgroups.forEach((subgroup) => {
                            updatedSelectedItem.subGroupId = subgroup.subGroupId;

                            if (subgroup.teams) {
                                subgroup.teams.forEach((team) => {
                                    updatedSelectedItem.teamId = team.teamId;
                                });
                            }
                        });
                    }
                });
            });

            dispatch(setFormValue({
                name: 'groups',
                value: updatedSelectedItem
                    ? [
                        {
                            groupId: updatedSelectedItem.groupId,
                            subGroupId: updatedSelectedItem.subGroupId,
                            teamId: updatedSelectedItem.teamId,
                        },
                    ]
                    : [],
            }));
        } else if (name === 'type') {
            setSelectedContentType(value);
            if (!isEditMode || !file.ContentType || (isEditMode && value !== mapMimeTypeToKey(file.ContentType))) {
                setFile({});
            }
        } else if (name === 'link') {
            dispatch(setFormValue({ name: 'link', value }));
            setLinkVerified(false);
            setLinkVerificationError('');
        } else {
            dispatch(setFormValue({ name, value }));
        }
    };

    const onAddLinkHandler = () => {
        setIsLinkInputVisible(true);
    };

    const isValidUrl = (str) => {
        const trimmed = (str || '').trim();
        if (!trimmed) return false;
        try {
            const url = new URL(trimmed);
            return url.protocol === 'http:' || url.protocol === 'https:';
        } catch {
            return false;
        }
    };

    const handleVerifyLink = async () => {
        const raw = (contentManagementState.link || '').trim();
        setLinkVerificationError('');
        if (!raw) {
            setLinkVerificationError('Please enter a link.');
            return;
        }
        if (!isValidUrl(raw)) {
            setLinkVerificationError('Please enter a valid URL (e.g. https://example.com).');
            return;
        }
        setLinkVerifying(true);
        setLinkVerified(true);
        setLinkVerificationError('');
        try {
            const response = await fetch(raw, { method: 'HEAD', mode: 'cors', redirect: 'follow' });
            if (response.ok || response.status >= 400) {
                // Reachable; verification complete
            } else {
                setLinkVerificationError('Could not verify reachability; link format is valid.');
            }
        } catch {
            setLinkVerificationError('Link format is valid. Could not reach server (e.g. CORS). You can save.');
        } finally {
            setLinkVerifying(false);
        }
    };

    const renderFilePreview = (file) => {
        if (!file || (!file.ContentType && !file.type)) {
            return null;
        }

        const fileUrl = file.url || file.SignedUrl;
        const contentType = file.ContentType || file.type
        const fileTypeCategory = contentType.split('/')[0];

        switch (fileTypeCategory) {
            case 'image':
                return <Image src={fileUrl} alt="Preview" fluid />;
            case 'video':
                return (
                    <video className={styles.filePreview} controls>
                        <source src={fileUrl} type={contentType} />
                        Your browser does not support the video tag.
                    </video>
                );
            case 'audio':
                return (
                    <audio controls>
                        <source src={fileUrl} type={contentType} />
                        Your browser does not support the audio tag.
                    </audio>
                );
            case 'application':
                return contentType === 'application/pdf' ? (
                    <div className={styles.filePreview}>
            
                        <iframe
                            src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                            width="100%"
                            height="600px"
                            style={{ 
                                border: '1px solid #ddd', 
                                borderRadius: '4px',
                                backgroundColor: 'white'
                            }}
                            title="PDF Preview"
                            onLoad={(e) => {
                                // Handle iframe load errors
                                try {
                                    const iframe = e.target;
                                    if (iframe.contentDocument && iframe.contentDocument.body) {
                                        // PDF loaded successfully
                                    }
                                } catch (error) {
                                    console.log('PDF preview not available in iframe');
                                }
                            }}
                        >
                            <p>Your browser does not support PDF preview. 
                                <a href={fileUrl} target="_blank" rel="noopener noreferrer">Click here to view the PDF</a>
                            </p>
                        </iframe>
                    </div>
                ) : (
                    <div className={styles.filePreview}>
                        <p>No preview available for this application type.</p>
                    </div>
                );
            default:
                return (
                    <div className={styles.filePreview}>
                        <p>No preview available for this file type.</p>
                    </div>
                );
        }
    };

    const mapMimeTypeToKey = (mimeType) => {
        if (mimeType.startsWith('image/')) return 'image';
        if (mimeType.startsWith('audio/')) return 'audio';
        if (mimeType === 'video/mp4') return 'video';
        if (mimeType === 'application/pdf') return 'pdf';
        return 'link';
    };


    const getUploadComponent = () => {
        if (selectedContentType && selectedContentType !== 'link') {
            return (
                <Form.Group className="mb-3">
                    <Form.Label>Upload File</Form.Label>
                    <FileUpload 
                        fileType={selectedContentType} 
                        onDrop={handleFileDrop}
                        onValidationError={handleFileValidationError}
                    />
                    {fileValidationError && (
                        <div className="text-danger mt-2" style={{ fontSize: '0.875rem' }}>
                            <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" style={{ marginRight: '8px' }} />
                            {fileValidationError}
                        </div>
                    )}
                </Form.Group>
            );
        } else if (selectedContentType === 'link') {
            return (
                <Form.Group className="mb-3">
                    <Button className={`${styles.AddLinkButton} mb-2`} onClick={onAddLinkHandler}>
                        <UnionIcon /> Add Link
                    </Button>
                    {isLinkInputVisible && (
                        <>
                            <InputGroup className="mb-3">
                                <Form.Control
                                    name="link"
                                    className={`${styles.LinkComponent}`}
                                    value={contentManagementState.link || ''}
                                    onChange={onInputChange}
                                    placeholder="https://example.com"
                                    aria-label="Link"
                                />
                                <Button
                                    variant="outline-secondary"
                                    className={styles.CheckButton}
                                    onClick={handleVerifyLink}
                                    disabled={linkVerifying}
                                >
                                    {linkVerifying ? 'Verifying…' : <CheckIcon />}
                                </Button>
                            </InputGroup>
                            {linkVerified && !linkVerificationError && (
                                <div className="text-success mb-2" style={{ fontSize: '0.875rem' }}>
                                    <FontAwesomeIcon icon="fa-solid fa-circle-check" style={{ marginRight: '8px' }} />
                                    Link verified
                                </div>
                            )}
                            {linkVerificationError && (
                                <div className="mb-2" style={{ fontSize: '0.875rem', color: linkVerificationError.includes('valid') && linkVerified ? '#856404' : 'var(--bs-danger)' }}>
                                    <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" style={{ marginRight: '8px' }} />
                                    {linkVerificationError}
                                </div>
                            )}
                        </>
                    )}
                </Form.Group>
            );
        }
        return null;
    };


    const getSelectedOrganization = (selectedOrgId) => {
        return organizationsHierarchy.find((org) => org.organizationId === Number(selectedOrgId));
    };

    const getSelectedGroup = (selectedOrgId, selectedGroupId) => {
        const organization = getSelectedOrganization(selectedOrgId);
        if (organization && organization.groups.length > 0) {
            return organization.groups.find((group) => group.groupId === Number(selectedGroupId));
        }
        return null;
    };

    const handleOrganizationChange = (e) => {
        const selectedOrgId = e.target.value;
        setSelectedOrgId(selectedOrgId); // Update the selectedOrgId state
        selectedOrgIdRef.current = selectedOrgId;
        const organization = getSelectedOrganization(selectedOrgId);

        dispatch(setFormValue({ name: 'organizationId', value: organization?.organizationId }));
        dispatch(setFormValue({ name: 'organizationName', value: organization?.organizationName }));

        if (organization && organization.groups.length === 1) {
            const selectedGroup = organization.groups[0];
            setSelectedGroupId(selectedGroup.groupId);
            dispatch(setOrgGroups(organization.groups));
            dispatch(setFormValue({ name: 'groupId', value: selectedGroup.groupId }));
            handleGroupChange({ target: { value: selectedGroup.groupId } });
        } else {
            if (organization && organization.groups.length > 0) {
                const allGroups = [{ groupId: "", groupName: 'Select Group' }, ...organization.groups];
                dispatch(setOrgGroups(allGroups));
            } else {
                dispatch(setOrgGroups([{ groupId: 0, groupName: 'Select Group' }]));
                setSelectedGroupId('');
                dispatch(setFormValue({ name: 'groupId', value: '' }));
            }
        }
    };

    const handleGroupChange = (e) => {
        const selectedGroupId = e.target.value;
        setSelectedGroupId(selectedGroupId); // Update the selectedGroupId state
        const selectedOrgId = selectedOrgIdRef.current;
        const selectedGroup = getSelectedGroup(selectedOrgId, selectedGroupId);

        if (selectedGroup) {
            const { groupId, subgroups } = selectedGroup;
            const { subGroupId, teams } = subgroups ? subgroups[0] || {} : {};
            const { teamId } = teams ? teams[0] || {} : {};

            dispatch(setFormValue({ name: 'groupId', value: groupId }));
            dispatch(setFormValue({ name: 'subGroupId', value: subGroupId }));
            dispatch(setFormValue({ name: 'teamId', value: teamId }));
        } else {
            dispatch(setFormValue({ name: 'groupId', value: '' }));
            dispatch(setFormValue({ name: 'subGroupId', value: '' }));
            dispatch(setFormValue({ name: 'teamId', value: '' }));
        }
    };

    const onAddEditHandler = () => {

        if (formRef.current) {
            let formIsInvalid = formRef.current.checkValidity() === false;
            setValidated(true);
            if (formIsInvalid) {
                setShowErrorMessage(true);
                return;
            }
        }
        const isLinkContent = selectedContentType === 'link';
        if (isLinkContent) {
            if (!linkVerified || !(contentManagementState.link || '').trim()) {
                setShowErrorMessage(true);
                return;
            }
        } else {
            if (Object.keys(file).length === 0) {
                setShowErrorMessage(true);
                return;
            }
        }
        if (fileValidationError) {
            setShowErrorMessage(true);
            return;
        }
        setShowErrorMessage(false);
        const bodyForm = new FormData();
        const cleanedData = formatDataForSaving();

        const groupsArray = [{
            groupId: cleanedData.groupId,
            subGroupId: cleanedData.subGroupId,
            teamId: cleanedData.teamId
        }];
        if (Object.keys(file).length > 0) {
            bodyForm.append('file', file);
        } else if (isLinkContent && linkVerified && (contentManagementState.link || '').trim()) {
            bodyForm.append('linkUrl', (contentManagementState.link || '').trim());
        }
        //bodyForm.append('file', files[0]);
        if (isEditMode) {
            bodyForm.append('id', cleanedData.id);
        }
        bodyForm.append('name', cleanedData.name);
        bodyForm.append('description', cleanedData.description);
        bodyForm.append('location', "test");
        bodyForm.append('groups', JSON.stringify(groupsArray));

        const endpoint = isEditMode ? `content/${cleanedData.id}` : 'content';

        httpService({
            method: isEditMode ? 'put' : 'post',
            url: endpoint,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'multipart/form-data',
            },
            data: bodyForm,
        })
            .then((response) => {
                navigate('/content-management');
                dispatch(setReloadGrid(true));
                setFile({});
                dispatch(
                    showAlert({
                        header: "Success",
                        message: `Content ${isEditMode ? 'updated' : 'added'} successfully`,
                        type: 'success'
                    })
                );
            })
            .catch((error) => {
                let errorMessage = `Error ${isEditMode ? 'updating' : 'adding'} content`;
                if (error?.response?.data?.message?.includes('duplicate key value violates unique constraint')) {
                    errorMessage = "Content name already exists in group";
                }
                dispatch(
                    showAlert({
                        header: "Error",
                        message: errorMessage,
                        type: 'danger',
                        dismissable: true
                    })
                );
                console.error(`Error ${isEditMode ? 'updating' : 'adding'} content:`, error);
            });
    };

    const formatDataForSaving = () => {
        return removeUndefined(contentManagementState);
    };

    const removeUndefined = (obj) => {
        const excludedAttributes = ['files', 'isUpdateMode', 'orgGroups', 'link'];
        if (!isEditMode) {
            excludedAttributes.push('id');
        }

        const cleanedData = Object.fromEntries(
            Object.entries(obj).map(([key, value]) => {
                if (!excludedAttributes.includes(key) && value !== undefined) {
                    return [key, value];
                }
                return null;
            }).filter(entry => entry !== null)
        );

        return cleanedData;
    };

    return (
        <div className={managementStyles.MainCard}>
            <h3 className={managementStyles.ContentTitle}> {isEditMode ? 'Update Content' : 'Create Content'}</h3>
            <div className={managementStyles.HideScrollContainer}>
                <Row className="g-0">
                    <Col md={5}>
                        <Form className="ps-3" ref={formRef} noValidate validated={validated}>
                            {isAdmin && (<Form.Group className="mb-3">
                                <FloatingLabel controlId="floatingSelect" label="Organization*">
                                    <Form.Select
                                        value={selectedOrgId || ""}
                                        className={`${managementStyles.BorderedComponent}`}
                                        onChange={handleOrganizationChange}
                                        style={{ width: "300px" }}
                                        disabled={isEditMode}
                                        required
                                    >
                                        <option value="">Select</option>
                                        {organizationsHierarchy.map((organization) => (
                                            <option key={organization.organizationId} value={organization.organizationId}>
                                                {organization.organizationName}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        Please select an organization.
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>)}
                            <Form.Group className="mb-3">
                                <FloatingLabel controlId="floatingSelect" label="Group*">
                                    <Form.Select
                                        value={contentManagementState.groupId || ""}
                                        className={`${managementStyles.BorderedComponent}`}
                                        onChange={handleGroupChange}
                                        style={{ width: "300px" }}
                                        required
                                    >
                                        {orgGroups?.map((group) => (
                                            <option key={group.groupId} value={group.groupId}>
                                                {group.groupName}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        Please select a group.
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <FloatingLabel
                                    controlId="floatingInput"
                                    label="Name*"
                                    className="mb-3"
                                >
                                    <Form.Control
                                        name="name"
                                        placeholder="name"
                                        style={{ width: "300px" }}
                                        value={contentManagementState.name}
                                        onChange={onInputChange}
                                        className={`${managementStyles.BorderedComponent}`}
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Please enter a name.
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <FloatingLabel
                                    controlId="floatingTextarea2"
                                    label="Description"
                                    className="mb-3"
                                >
                                    <Form.Control
                                        name="description"
                                        value={contentManagementState.description}
                                        onChange={onInputChange}
                                        as="textarea"
                                        style={{ width: "300px" }}
                                        placeholder='Description'
                                        rows={2}
                                        className={`${managementStyles.BorderedComponentTextArea}`}
                                    />
                                </FloatingLabel>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <FloatingLabel controlId="floatingSelect" label="Content Type*">
                                    <Form.Select
                                        name="type"
                                        style={{ width: "300px" }}
                                        value={isEditMode && file.ContentType ? mapMimeTypeToKey(file.ContentType) : selectedContentType}
                                        onChange={onInputChange}
                                        className={`${managementStyles.BorderedComponent}`}
                                        required
                                    >
                                        <option value="">Select Content Type</option>
                                        {dropdowns.contentType.values.map((code) => (
                                            <option key={code.id} value={code.display.toLowerCase()}>
                                                {code.display}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        Please select a content type.
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>
                            {selectedContentType && getUploadComponent()}
                        </Form>
                    </Col>
                    <Col md={7}>
                        {/* Preview for selected file */}
                        {Object.keys(file).length > 0 && (
                            <div className={styles.previewContainer}>
                                {renderFilePreview(file)}
                            </div>
                        )}
                    </Col>
                </Row>
            </div>
            <div className={managementStyles.ButtonsFooterContainerWithError}>
                {showErrorMessage &&
                    <div className="page-error">
                        <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" size="sm" style={{ color: "red" }} />
                        {fileValidationError
                            ? fileValidationError
                            : selectedContentType === 'link'
                                ? 'Group, Name, and a verified link are required to proceed'
                                : 'Group, Name, and Content are required to proceed'}
                    </div>
                }
                <div style={{ display: "flex", columnGap: "10px" }}>
                    <Button variant="primary" className={managementStyles.CreateButton} onClick={onAddEditHandler}>
                        {isEditMode ? 'Update' : 'Add'}
                    </Button>

                    <Button variant="secondary" className={managementStyles.BackButton} onClick={onBackHandler}>
                        Cancel
                    </Button>
                </div>

            </div>
        </div>
    );
};

export default AddEditContent;
